/**
 * EmailDupPoBanner — amber, non-blocking warning shown inside the opened
 * email when the pipeline created the draft anyway despite the (customer,
 * PO) already matching one or more cross-thread orders. It mirrors the
 * compact inbox "Duplicate PO" badge but, with room to spare, shows the
 * full reason inline instead of hiding it in a hover tooltip.
 *
 * Same-thread + same-PO duplicates never reach here — the relationship
 * FSM dedups those before an order is ever created.
 */
import React from 'react';
import styles from './EmailDupPoBanner.module.css';

interface EmailDupPoBannerProps {
  /** Human summary from the inbox API (e.g. "PO 1125786 matches order SO12345 (submitted)"). */
  warning: string | null | undefined;
}

const EmailDupPoBanner: React.FC<EmailDupPoBannerProps> = ({ warning }) => {
  if (!warning) return null;

  return (
    <div className={styles.banner} role="alert" data-testid="email-dup-po-banner">
      <span className={styles.icon} aria-hidden="true">
        &#9888;
      </span>
      <div className={styles.body}>
        <div className={styles.title}>Duplicate PO</div>
        <div className={styles.text}>{warning}</div>
        <div className={styles.note}>
          The draft was still created — review whether this duplicates an existing order.
        </div>
      </div>
    </div>
  );
};

export default EmailDupPoBanner;
